import React, { useState, useEffect } from 'react';
import { 
  Plug, 
  Search, 
  Calendar, 
  MapPin, 
  Check, 
  FileText, 
  Wrench, 
  Clock, 
  ChevronUp, 
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Card } from '@shared/ui/card';
import { Input } from '@shared/ui/input';
import { Button } from '@shared/ui/button';
import { Badge } from '@shared/ui/badge';
import { GuestPortalTopnav } from '../../../Topnav';
import { AnnouncementBanner } from '../../../AnnouncementBanner';
import { useApplicationStatus } from '../../../../hooks';
import { getLoginDataFromStorage, getRemoteUtilityId } from "@shared/utils/loginUtils";
import { useConsumerDetail } from '../../../../../auth/hooks';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';

export default function ApplicationStatus() {
  const { toast } = useToast(); 
 const { t } = useTranslation();
  const { remoteUtilityId: loginUtilityId } = getLoginDataFromStorage();
  const remoteUtilityId = loginUtilityId || getRemoteUtilityId();
  const [searchId, setSearchId] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const { data: consumerResponse, isSuccess: isConsumerSuccess, isError: isConsumerError, error: consumerError, isFetching: isFetchingConsumer } = useConsumerDetail(
    { consumer_no: searchId, remote_utility_id: remoteUtilityId },
    { enabled: hasSearched && !!searchId }
  );

  const { data: statusResponse, isLoading: isAppStatusLoading } = useApplicationStatus(
    { consumer_no: searchId, remote_utility_id: remoteUtilityId },
    { enabled: hasSearched && !!searchId && isConsumerSuccess }
  );

  const isLoading = isFetchingConsumer || isAppStatusLoading;

  useEffect(() => {
    if (isConsumerError) {
      const err = consumerError as any;
      let errorMessage = "Could not find application details.";
      
      if (err?.response?.data?.message) {
        errorMessage = typeof err.response.data.message === 'string' 
          ? err.response.data.message 
          : err.response.data.message.message || JSON.stringify(err.response.data.message);
      } else if (err?.message) {
        errorMessage = err.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      setHasSearched(false);
    }
  }, [isConsumerError, consumerError, toast]);

  const activities = statusResponse?.data?.activities || [];

  const getIconForStatus = (title: string, status: string) => {
    if (status.toLowerCase() === 'completed') return Check;
    if (title.toLowerCase().includes('installation')) return Wrench;
    if (title.toLowerCase().includes('activation')) return Plug;
    if (title.toLowerCase().includes('approved')) return FileText;
    return Clock;
  };

  const dynamicTimeline = activities.map((act: any, idx: number) => ({
    id: idx + 1,
    title: act.title,
    date: act.date,
    description: act.status === 'Completed' ? t("Completed successfully") : t("Awaiting completion"),
    status: act.status.toLowerCase(),
    icon: getIconForStatus(act.title, act.status)
  }));

  const consumerData = consumerResponse?.data?.result || consumerResponse?.result || consumerResponse?.data || statusResponse?.data || {};
  const consumerName = `${consumerData?.firstName || ''} ${consumerData?.lastName || ''}`.trim() || '';
  const serviceAddress = consumerData?.territoryData?.service?.address
    || consumerData?.addressData?.service?.ADDRESS 
    || consumerData?.addressData?.service?.address 
    || '';
  const servicesRequest = consumerData?.utilityService?.join(', ') || '';
  const submittedOn = consumerData?.createdDate 
    ? new Date(consumerData.createdDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    : '';
  
  const completedActivities = activities.filter((a: any) => a.status === 'Completed');
  const currentStageTitle = completedActivities.length > 0 
    ? completedActivities[completedActivities.length - 1].title 
    : (activities[0]?.title || t("Submitted"));

  const handleTrack = () => {
    if (searchId.trim()) {
      setHasSearched(true);
    }
  };

  const handlePillClick = (id: string) => {
    setSearchId(id);
    setHasSearched(true);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden ">
      <div className="shrink-0 sticky top-0 z-50 bg-white shadow-sm">
        <GuestPortalTopnav />
        
        {/* Banner */}
        <AnnouncementBanner />
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto w-full">
        <div className="w-full px-4 py-12">
        
        {/* Header */}
        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 bg-[#e8f0fe] rounded-2xl flex items-center justify-center shrink-0">
            <Plug className="w-6 h-6 text-[#1e5eff]" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight">{t("Application Status")}</h1>
            <p className="text-slate-500 font-medium mt-1">{t("Track the progress of your new connection application")}</p>
          </div>
        </div>

        {/* Search Card */}
        <Card className="p-8 border border-slate-200 shadow-sm rounded-[20px] bg-white">
          <div className="mb-2">
            <label className="text-sm font-bold text-slate-700">{t("Application ID / Phone Number")}</label>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input 
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="pl-12 py-6 text-base rounded-xl border-slate-200 bg-white" 
              placeholder={t("Enter Application ID or Phone Number")}
              onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
            />
          </div>
          <p className="text-[13px] text-slate-400 font-medium mb-6">
            {t("We use this to fetch your current application status")}
          </p>

          <Button 
            onClick={handleTrack}
            disabled={isLoading}
            className="w-full "
          >
            {isLoading ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Tracking...</>
            ) : (
              <><Search className="w-5 h-5 mr-2" /> Track Application</>
            )}
          </Button>


        </Card>

        {!hasSearched ? (
          /* Info Card (Before Search) */
          <div className="bg-[#f0f5ff] border border-blue-100 rounded-xl p-6 flex gap-4 mt-8 shadow-sm">
            <div className="w-10 h-10 bg-[#1e5eff] rounded-full flex items-center justify-center shrink-0">
              <Plug className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-[#1e5eff] font-bold text-lg mb-3">{t("How to track your application?")}</h3>
              <ul className="space-y-3 text-[14px] text-[#1e5eff]/90 font-medium">
                <li className="flex items-start">
                  <span className="mr-3 mt-1.5 w-1.5 h-1.5 bg-[#1e5eff] rounded-full shrink-0"></span>
                  <span>{t("Enter your")} <strong className="text-[#1e5eff]">{t("Application ID")}</strong> {t("provided during submission.")}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1.5 w-1.5 h-1.5 bg-[#1e5eff] rounded-full shrink-0"></span>
                  <span>{t("Alternatively, you can use your registered")} <strong className="text-[#1e5eff]">{t("Phone Number")}</strong> {t("to track the")} <strong className="text-[#1e5eff]">{t("latest application")}</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 mt-1.5 w-1.5 h-1.5 bg-[#1e5eff] rounded-full shrink-0"></span>
                  <span>{t("Click on 'Track Status' to view the progress timeline.")}</span>
                </li>
              </ul>
              <p className="text-[14px] text-[#1e5eff]/90 font-medium mt-5">
                {t("Haven't applied yet?")} <a href="#" className="font-bold underline text-[#1e5eff]">{t("Apply Now")}</a>
              </p>
            </div>
          </div>
        ) : (
          /* Search Results Card */
          <Card className="mt-8 rounded-[20px] border border-slate-200 overflow-hidden shadow-sm">
            {/* Header */}
            <div className="p-6 bg-white flex justify-between items-start border-b border-slate-100">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#e8f0fe] rounded-xl flex items-center justify-center shrink-0">
                  <Plug className="w-6 h-6 text-[#1e5eff]" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-extrabold text-slate-800">{consumerData?.consumer_no || searchId}</h3>
                    <Badge className="bg-[#e8f0fe] text-[#1e5eff] hover:bg-[#e8f0fe] border-0 font-bold px-2 py-0.5">{t("NEW CONNECTION")}</Badge>
                  </div>
                  <p className="text-[15px] text-slate-600 font-medium">{consumerName}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 text-[13px] text-slate-400 font-medium">
                    <Calendar className="w-3.5 h-3.5" />
                    {t("Submitted")} {submittedOn}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-400 font-bold  tracking-wider mb-1.5">{t("CURRENT STAGE")}</p>
                <div className="flex items-center justify-end gap-1 text-[#1e5eff] font-bold text-[14px] cursor-pointer hover:underline">
                  {currentStageTitle} <ChevronUp className="w-4 h-4 ml-1" />
                </div>
              </div>
            </div>

            {/* Info Columns */}
            <div className="grid grid-cols-3 gap-6 p-6 bg-white border-b border-slate-100">
              <div>
                <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400  tracking-wider mb-2">
                  <MapPin className="w-3.5 h-3.5" /> {t("SERVICE ADDRESS")}
                </div>
                <p className="text-[13px] font-semibold text-slate-700 leading-snug">{serviceAddress}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400  tracking-wider mb-2">
                  <RefreshCw className="w-3.5 h-3.5" /> {t("SERVICES REQUESTED")}
                </div>
                <p className="text-[13px] font-semibold text-slate-700 leading-snug">{servicesRequest}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400  tracking-wider mb-2">
                  <Calendar className="w-3.5 h-3.5" /> {t("SUBMITTED ON")}
                </div>
                <p className="text-[13px] font-semibold text-slate-700 leading-snug">{submittedOn}</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="p-6 bg-white">
              <h4 className="text-[11px] font-bold text-slate-400  tracking-wider mb-8">{t("APPLICATION PROGRESS")}</h4>
              
              <div className="space-y-0 pl-2">
                {isLoading ? (
                  <div className="py-8 text-center text-slate-500 font-medium">{t("Loading timeline...")}</div>
                ) : dynamicTimeline.length > 0 ? (
                  dynamicTimeline.map((stage: any, idx: number) => {
                    const isLast = idx === dynamicTimeline.length - 1;
                    const isCompleted = stage.status === 'completed';
                    const nextIsCompleted = !isLast && dynamicTimeline[idx + 1].status === 'completed';
                  
                  return (
                    <div key={stage.id} className="flex group">
                      <div className="flex flex-col items-center mr-5">
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 shrink-0 ${isCompleted ? 'bg-[#22c55e] text-white' : 'bg-slate-100 text-slate-400'}`}>
                          <stage.icon className="w-4 h-4 stroke-[3]" />
                        </div>
                        {/* Line */}
                        {!isLast && (
                          <div 
                            className={`w-0.5 h-full ${isCompleted && nextIsCompleted ? 'bg-[#22c55e]' : 'bg-slate-100'}`} 
                            style={{ minHeight: '3.5rem' }}
                          ></div>
                        )}
                      </div>
                      <div className="pb-8 pt-1">
                        <div className="flex items-center gap-3">
                          <span className={`font-bold text-[15px] ${isCompleted ? 'text-[#166534]' : 'text-slate-400'}`}>
                            {stage.title}
                          </span>
                          {isCompleted ? (
                            <span className="text-[12px] font-bold text-[#16a34a]">{stage.date}</span>
                          ) : (
                            <span className="text-[10px] bg-slate-100 text-slate-400 px-2 py-0.5 rounded-md  font-bold tracking-wide">
                              {stage.date}
                            </span>
                          )}
                        </div>
                        <p className={`text-[13px] font-medium mt-1.5 ${isCompleted ? 'text-slate-600' : 'text-slate-400'}`}>
                          {stage.description}
                        </p>
                      </div>
                    </div>
                  );
                })) : (
                  <div className="py-8 text-center text-slate-500 font-medium">{t("No progress data available")}</div>
                )}
              </div>
            </div>
          </Card>
        )}

        </div>
      </div>
    </div>
  );
}